import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, of, throwError } from 'rxjs';
import { ApiService } from 'src/app/core/api/api.service';
import { Ordenes } from 'src/app/interfaces/transacciones/ordenes.interface';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class OrdenesService {

    private enviosUrl: string = environment.enviosUrl;
    constructor(
      private http: HttpClient,
      private api: ApiService
    ) {}

    private normalizeMoneyValue(value: any): string | null {
      if (value === undefined || value === null) {
        return null;
      }

      const raw = String(value).trim();
      if (!raw || ['undefined', 'null', 'nan'].includes(raw.toLowerCase())) {
        return null;
      }

      const normalized = raw
        .replace(/\$/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');

      if (!/^\d+(\.\d{1,4})?$/.test(normalized)) {
        return null;
      }

      const numeric = Number(normalized);
      if (!Number.isFinite(numeric) || numeric < 0) {
        return null;
      }

      return normalized;
    }

    private firstValidMoneyValue(...values: any[]): string | null {
      for (const value of values) {
        const normalized = this.normalizeMoneyValue(value);
        if (normalized !== null) {
          return normalized;
        }
      }

      return null;
    }


    /* -------------------------------------------------------------------------- */
    /*                         OBTENER ORDENES SERVER SIDE                        */
    /* -------------------------------------------------------------------------- */
    cargarOrdenPaginadoFecha(limit, ofsset, busqueda, atributoBusqueda,perfil, sucursal, estado, tipoBusqueda) {
        return this.api.post<any>('/transactions/orders/list', {
          limit: Number(limit) || 10,
          offset: Number(ofsset) || 0,
          search: busqueda || 'vacio',
          searchType: atributoBusqueda || 'NOM_CLI',
          status: estado,
          searchMode: tipoBusqueda || 'normal'
        }).pipe(
          map((resp) => ({
            ...resp,
            total: resp.pagination?.total ?? 0
          }))
        );
    
    }
    
    /* -------------------------------------------------------------------------- */
    /*                            ASIGNAR ORDEN FISICA                            */
    /* -------------------------------------------------------------------------- */
    asignarOrdenFisica(
      atributoSecuencia,
      codigoOrden,
      codigoDoctor,
      nombreDoctor,
      nombreCliente,
      secuencialOrden, 
      token) {
  
      return this.api.post('/transactions/orders/sequence/physical/assign', {
        orderId: codigoOrden,
        doctorCode: codigoDoctor,
        doctorName: nombreDoctor,
        patientName: nombreCliente,
        sequence: atributoSecuencia
      }).pipe(map(() => 'ok'));
  
    }

    /* -------------------------------------------------------------------------- */
    /*                          ELIMINAR SECUENCIA FISICA                         */
    /* -------------------------------------------------------------------------- */
    eliminarSecuenciaFisica(
      orden,
      secuenciaFisica,
      token) {
  
      return this.api.post('/transactions/orders/sequence/physical/remove', {
        orderId: orden,
        sequence: secuenciaFisica
      }).pipe(map(() => 'ok'));
  
    }
  
     /* -------------------------------------------------------------------------- */
    /*                          ELIMINAR SECUENCIA FISICA                         */
    /* -------------------------------------------------------------------------- */
    eliminarOrden(
      orden,
      token) {
  
      return this.api.post('/transactions/orders/cancel', {
        orderId: orden
      }).pipe(map(() => 'ok'));
  
    }
  

    /* -------------------------------------------------------------------------- */
    /*                               CONTAR ORDENES                               */
    /* -------------------------------------------------------------------------- */
    contarOrden(item,orden, token){


      return this.api.post('/transactions/orders/notification/increment', {
        field: item,
        orderId: orden
      }).pipe(map(() => 'ok'));
      

    }

    /* -------------------------------------------------------------------------- */
    /*                         SOLICITAR CAMBIO DE PRECIO                         */
    /* -------------------------------------------------------------------------- */
    solicitarCambioPrecio(nombreUsuario, idUsuario,precioSolicitado, nombreServicioSolicitado,precioNormalSolicitad,nombreSucursal, 
      idSucursal,
      tipo,
      precioNormal,
      tipoMensaje,
      itemsOrden,
      doctor,
      token){

      const requestedPrice = this.normalizeMoneyValue(precioSolicitado);
      const normalPrice = this.firstValidMoneyValue(precioNormalSolicitad, precioNormal) || '0.00';

      if (!requestedPrice) {
        return of({
          status: false,
          message: 'Ingrese o seleccione un precio válido.',
          errors: {
            requestedPrice: ['Precio solicitado inválido'],
          },
          errorCode: 1201,
        });
      }

      return this.api.post('/company/price-requests/create', {
        userName: nombreUsuario,
        userId: idUsuario,
        requestedPrice,
        serviceName: nombreServicioSolicitado,
        normalPrice,
        principalPrice: normalPrice,
        branchName: nombreSucursal,
        branchId: idSucursal,
        serviceType: tipo,
        messageType: tipoMensaje,
        itemsCount: itemsOrden,
        doctor
      }).pipe(
        catchError((err: HttpErrorResponse) => of({
          status: false,
          message: err?.error?.message || 'La solicitud contiene datos inválidos',
          errors: err?.error?.errors || null,
          errorCode: err?.error?.errorCode || err?.status || null,
        }))
      );
      

    }


    /* -------------------------------------------------------------------------- */
    /*                              ENCRIPTAR CODIGO                              */
    /* -------------------------------------------------------------------------- */
    encriptarCodigo(_token, codigo) {
      return this.api.post<{ hash: string }>(
        '/transactions/orders/validation-code/hash',
        { code: codigo }
      ).pipe(
        // Conserva temporalmente la respuesta legacy para no romper consumidores.
        map(resp => [{ BASE: resp.hash }]),
        catchError(err => of(err))
      )
  
    }



    /* -------------------------------------------------------------------------- */
    /*                                GUARDAR ORDEN                               */
    /* -------------------------------------------------------------------------- */
    guardarOrden(
      idPaciente,
      nuevoTelefonoPaciente,
      nuevoTelefonoDoctor, 
      cedulaDoctor,
      codigoDoctor,
      nombreDoctor, 
      telefonoDoctor,
      detalleOrden,
      idSucursal,
      secuencial,
      metodoPago,
      codigoTarjeta:any,
      nombreTarjeta:any,
      analisisCefa,
      citaPrevia,
      fechaCitaPrevia,
      token,
      reserved,
      idOferta = "",
      convenio = 0,
      nombreSucursal = "",
      nombreUsuario = ""
    ){
      

      return this.api.post<{ order: { id: number; numero: string } }>(
        '/transactions/orders/create',
        {
          idPaciente,
          nuevoTelefonoPaciente,
          nuevoTelefonoDoctor,
          cedulaDoctor,
          codigoDoctor,
          telefonoDoctor,
          nombreDoctor,
          detalleOrden: this.parseJson(detalleOrden),
          analisisCefa,
          citaPrevia,
          fechaCitaPrevia,
          reserved: Number(reserved) || 0,
          convenio: Number(convenio) || 0,
          nombreSucursal,
          nombreUsuario
        }
      ).pipe(map(({ order }) => `${order.numero},${order.id}`));
      

    }

    /* -------------------------------------------------------------------------- */
    /*                               GUARDAR FACTURA                              */
    /* -------------------------------------------------------------------------- */
    generarFacturaVersion2(idOrden, idUsuario, nombreUsuario, metodoPago,subtotalImpuesto,
      subtotalSinImpuesto,
      subtotal,
      iva,
      total, 
      secuencial,
      dineroRecibido,
       dineroEntregado,
       idSucursal,
        nombreSucursal, 
      codigoTarjeta,
      nombreTarjeta,
      jsonPagos,
       token,
      idOferta = "",
      emergency = false,
      observacion
      ){
      const normalizedTotal = this.firstValidMoneyValue(total, subtotal, subtotalSinImpuesto, subtotalImpuesto, '0') || '0';
      const cashReceived = this.firstValidMoneyValue(dineroRecibido, normalizedTotal, '0') || '0';
      const cashReturned = this.firstValidMoneyValue(dineroEntregado, '0') || '0';

      return this.api.post<{
        invoice: {
          accessKey: string;
          invoiceNumber: string;
          invoiceType: string;
          isLaboratory: string;
          legacyResponse: string;
          transactionId: number;
        }
      }>('/transactions/invoices/create', {
        idOrden,
        idUsuario,
        nombreUsuario,
        metodoPago,
        subtotalImpuesto,
        subtotalSinImpuesto,
        subtotal,
        iva,
        total,
        secuencial,
        dineroRecibido: cashReceived,
        dineroEntregado: cashReturned,
        idSucursal,
        nombreSucursal,
        codigoTarjeta,
        nombreTarjeta,
        jsonPagos,
        idOferta,
        emergency,
        observacion
      }).pipe(
        map(({ invoice }) => invoice.legacyResponse)
      );
      

    }

       actualizarOrdenConvenio(idOrden,
       _token,
        nombreUsuario,
      ){
      return this.api.post<{ legacyResponse: string }>(
        '/transactions/agreement-orders/update',
        {
          orderId: Number(idOrden),
          responsible: nombreUsuario || ''
        }
      ).pipe(map(resp => resp.legacyResponse));
    }
    /* -------------------------------------------------------------------------- */
    /*                             ENVIAR FACTURA SRI                             */
    /* -------------------------------------------------------------------------- */
    enviarFactura(claveAcceso, emailCliente, nombreCliente, token){

      return this.api.post<{ sri: { status: string; number?: string; authorizedAt?: string } }>(
        '/transactions/electronic-documents/send-sri',
        { claveAcceso }
      ).pipe(map(({ sri }) => {
        const status = sri?.status || 'authorized';
        const autorizado = status === 'authorized';
        const mensaje = autorizado
          ? 'Factura enviada y autorizada correctamente'
          : 'Factura enviada al SRI. Revise el estado de autorización.';

        return JSON.stringify({
          2: mensaje,
          7: autorizado ? 'mail enviado' : mensaje,
          authorizedAt: sri?.authorizedAt,
          message: mensaje,
          number: sri?.number,
          status,
          success: autorizado,
        });
      }), catchError((err) => {
        const mensaje = err?.error?.message
          || err?.message
          || 'Factura generada. No fue posible completar la autorización SRI en este momento.';

        return of(JSON.stringify({
          2: mensaje,
          7: mensaje,
          errorCode: err?.error?.errorCode,
          errors: err?.error?.errors,
          message: mensaje,
          status: 'sri_error',
          success: false,
        }));
      }));


    }

    generarReciboInterno(token, numeroFactura, tipoFactura, idOrden){
      return this.api.postBlob('/transactions/electronic-documents/internal-receipt', {
        idOrden: String(idOrden ?? ''),
        numeroFactura: String(numeroFactura ?? ''),
        tipoFactura: String(tipoFactura ?? '')
      });
    }

    generarReciboConvenio(token, idOrden){
      return this.api.postBlob('/transactions/electronic-documents/agreement-receipt', {
        orderId: Number(idOrden)
      });
    }

    generarFacturaClienteNode(token, numeroFactura, tipoFactura, idOrden){
      return this.api.postBlob('/transactions/electronic-documents/customer-ticket', {
        idOrden: String(idOrden ?? ''),
        numeroFactura: String(numeroFactura ?? ''),
        tipoFactura: String(tipoFactura ?? '')
      });
    }

    generarTicketMuestraLaboratorio(token, numeroFactura, modo = 'nuevo', tipoFactura = 'Electronica', idOrden = ''){
      return this.api.postBlob('/transactions/electronic-documents/lab-sample-ticket', {
        idOrden: String(idOrden ?? ''),
        modo: String(modo ?? ''),
        numeroFactura: String(numeroFactura ?? ''),
        tipoFactura: String(tipoFactura ?? '')
      });
    }

    generarOrdenTransferencia(token, orden, doctor, telefono){
      return this.api.postBlob('/transactions/electronic-documents/order-transfer', {
        doctor: String(doctor ?? ''),
        orden: String(orden ?? ''),
        telefono: String(telefono ?? '')
      });
    }


    /* -------------------------------------------------------------------------- */
    /*                              ENCRIPTAR CODIGO                              */
    /* -------------------------------------------------------------------------- */
    mostrarOrdenesNormal(token ,normal, item, valor) {
      return this.api.post<{ rows: any[] }>('/transactions/orders/find', {
        type: normal,
        field: item,
        value: valor
      });
  
    }


    /* -------------------------------------------------------------------------- */
    /*                                GUARDAR ORDEN                               */
    /* -------------------------------------------------------------------------- */
    editarOrden(idPaciente, nuevoTelefonoPaciente,nuevoTelefonoDoctor, cedulaDoctor,codigoDoctor,nombreDoctor, 
      telefonoDoctor,
      detalleOrden,
      idSucursal,
      secuencial,
      idOrden,
      numeroOrden,
      metodoPago,
      codigoTarjeta,
      nombreTarjeta,
      analisisCefa,
      token,
      reserved
    ){
      
  

      return this.api.post<{ order: { id: number; numero: string } }>(
        '/transactions/orders/update',
        {
          idPaciente,
          nuevoTelefonoPaciente,
          nuevoTelefonoDoctor,
          cedulaDoctor,
          codigoDoctor,
          telefonoDoctor,
          nombreDoctor,
          detalleOrden: this.parseJson(detalleOrden),
          analisisCefa,
          idOrden,
          numeroOrden,
          reserved: Number(reserved) || 0
        }
      ).pipe(map(({ order }) => `${order.numero},${order.id}`));
      

    }

    /* -------------------------------------------------------------------------- */
    /*                   CARGAR IMAGENES CUANDO SE ESTÁ EDITANTO                  */
    /* -------------------------------------------------------------------------- */
    cargarImagenesEditar(idOrden, ordenFisica, detalleOrden, _token) {
        const servicios = JSON.parse(detalleOrden);
        return this.api.post('/transactions/orders/results/edit/upload', {
            orderId: idOrden,
            physicalOrder: ordenFisica || '',
            detail: servicios,
        });
    }

    
    scargarImagenesAnalisis(idOrden, _ordenFisica, detalleOrden, _token) {
        const servicios = JSON.parse(detalleOrden);
        return this.api.post('/transactions/orders/results/analysis/upload', {
            orderId: idOrden,
            detail: servicios,
        });
    }

    
    /* -------------------------------------------------------------------------- */
    /*                      CARGAR IMAGENES POR DEPARTAMENTE                      */
    /* -------------------------------------------------------------------------- */
    cargarImagenesPorDeptamento(idOrden, ordenFisica, detalleOrden, _token, tipo) {
      const servicios = JSON.parse(detalleOrden);
      servicios.forEach((s: any, i: number) => {
        s.keyServicio = `${s.id}_${i}_${Date.now()}`;
      });

      return this.api.post('/transactions/orders/results/department/upload', {
        orderId: idOrden,
        physicalOrder: ordenFisica || '',
        detail: servicios,
        type: tipo,
        device: this.obtenerDispositivo(),
        platform: (navigator as any).userAgentData?.platform || (navigator as any).platform || '',
        language: navigator.language || '',
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      });
    }

    /* -------------------------------------------------------------------------- */
    /*                    CARGAR IMAGENES PENDIENTE FACTURADOS                    */
    /* -------------------------------------------------------------------------- */
    cargarImagenesPendientes(idOrden,detalleOrden, token, estado_corporal = 0){
      return this.api.post('/transactions/orders/results/corporal/upload', {
        orderId: idOrden,
        detail: JSON.parse(detalleOrden),
        corporalStatus: Number(estado_corporal) || 0,
        device: this.obtenerDispositivo(),
        platform: (navigator as any).userAgentData?.platform || navigator.platform || '',
        language: navigator.language || '',
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      }).pipe(map(() => 'ok'));
    }
    
    enviarResultados(idOrden, tipo){

      // El endpoint PHP lee $_POST["idOrden"] y $_POST["tipo"], por lo que el
      // cuerpo debe ir como x-www-form-urlencoded (NO JSON). El token va en el
      // header X-API-Token ($_SERVER["HTTP_X_API_TOKEN"]).
      const body = new HttpParams()
        .set('idOrden', idOrden)
        .set('tipo', tipo);

      const headers = new HttpHeaders({
        'X-API-Token': environment.senderApiToken,
      });

      const url = `${this.enviosUrl}/sender-patient/sender.php`;

      return this.http.post(url, body, {
        headers,
        responseType: 'text'
      });
    }

    crearEnlaceOrdenPaciente(orderId) {
      return this.api.post<{
        doctorName: string;
        orderNumber: string;
        patientName: string;
        patientPhone: string;
        token: string;
      }>('/transactions/orders/share-link/create', { orderId });
    }


    /* -------------------------------------------------------------------------- */
    /*                              ACTUALIZAR DOCTOR                             */
    /* -------------------------------------------------------------------------- */
    actualizarDoctor(codigoDoctor,orden, numeroOrden, secuenciaFisica, token){


      return this.api.post('/transactions/orders/doctor/update', {
        doctorCode: codigoDoctor,
        orderNumber: numeroOrden,
        orderId: orden,
        physicalSequence: secuenciaFisica
      }).pipe(map(() => 'ok'));
      

    }


    /* -------------------------------------------------------------------------- */
    /*                              OBTENER PAQUETES                              */
    /* -------------------------------------------------------------------------- */
    obtenerPaquetes(token ,normal, item, valor) {
      if (item == null || item === 'null') {
        return this.api.post<{ rows: any[] }>(
          '/transactions/orders/packages/list'
        );
      }

      return this.api.post<{ rows: any[] }>('/transactions/orders/find', {
        type: normal,
        field: item,
        value: valor
      });
  
    }

    
    /* -------------------------------------------------------------------------- */
    /*                            GUARDAR ODEN PAQUETE                            */
    /* -------------------------------------------------------------------------- */
    guardarOrdenPaquete(idPaciente, nuevoTelefonoPaciente,nuevoTelefonoDoctor, cedulaDoctor,codigoDoctor,nombreDoctor, 
      telefonoDoctor,
      detalleOrden,
      idSucursal,
      secuencial,
      numeroFactura,
      secuanciaFisica,
      secuenciasDisponibles,
      token,
      reserved){
      
  

      return this.api.post<{ order: { id: number; numero: string } }>(
        '/transactions/orders/package/create',
        {
          idPaciente,
          nuevoTelefonoPaciente,
          nuevoTelefonoDoctor,
          cedulaDoctor,
          codigoDoctor,
          telefonoDoctor,
          nombreDoctor,
          detalleOrden: this.parseJson(detalleOrden),
          numeroFactura,
          secuenciaFisica: secuanciaFisica,
          secuenciasDisponibles: this.parseJson(secuenciasDisponibles),
          analisisCefa: 'No',
          reserved: Number(reserved) || 0
        }
      ).pipe(map(({ order }) => `${order.numero},${order.id}`));
      

    }

    /* -------------------------------------------------------------------------- */
    /*                              ACTUALIZAR CORREO                             */
    /* -------------------------------------------------------------------------- */
    actualizarCorreoDoctor(codigoDoctor, idOrden,nuevoCorreoElectronico, token){
      
  

      return this.api.post('/transactions/orders/doctor/email/update', {
        doctorCode: codigoDoctor,
        orderId: idOrden,
        email: nuevoCorreoElectronico
      }).pipe(map(() => 'ok'));
      

    }


    /* -------------------------------------------------------------------------- */
    /*                           ENVIAR EMAIL TOMOGRAFÍA                          */
    /* -------------------------------------------------------------------------- */
    enviarEmailTomografiaNode(
      orderId,
      type: 'tomography' | 'dicom',
      link: string
    ) {
      return this.api.post('/transactions/orders/results/tomography/email/send', {
        link,
        orderId,
        type
      }).pipe(map(() => 'ok'));
    }

    enviarWhatsappTomografiaNode(
      orderId,
      type: 'tomography' | 'dicom',
      link: string
    ) {
      return this.api.post(
        '/transactions/orders/results/tomography/whatsapp/send',
        {
          link,
          orderId,
          type
        }
      ).pipe(map(() => 'ok'));
    }

    /* -------------------------------------------------------------------------- */
    /*                           BUSCAR SECUENCIA FISICA                          */
    /* -------------------------------------------------------------------------- */
    buscarSecuenciaFisica(token, tipo, item, valor) {
      if (tipo === 'secuencia-fisica-busqueda') {
        return this.api.post<any>(
          '/transactions/orders/sequence/physical/search',
          { search: valor, limit: 20 }
        );
      }

      return this.api.post<{ rows: any[] }>('/transactions/orders/find', {
        type: tipo,
        field: item,
        value: valor
      });
  
    }


    /* -------------------------------------------------------------------------- */
    /*                            GUARDAR ORDEN FISICA                            */
    /* -------------------------------------------------------------------------- */
    guardarOrdenFisica(idPaciente, nuevoTelefonoPaciente,nuevoTelefonoDoctor, cedulaDoctor,codigoDoctor,nombreDoctor, 
      telefonoDoctor,
      detalleOrden,
      idSucursal,
      secuencial,
      secuenciaFisica,
      metodoPago,
      codigoTarjeta,
      nombreTarjeta,
      analisisCefa,
      token,
      reserved
    
    ){
      
  

      return this.api.post<{ order: { id: number; numero: string } }>(
        '/transactions/orders/physical/create',
        {
          idPaciente,
          nuevoTelefonoPaciente,
          nuevoTelefonoDoctor,
          cedulaDoctor,
          codigoDoctor,
          telefonoDoctor,
          nombreDoctor,
          detalleOrden: this.parseJson(detalleOrden),
          secuenciaFisica,
          metodoPago,
          analisisCefa,
          reserved: Number(reserved) || 0
        }
      ).pipe(map(({ order }) => `${order.numero},${order.id}`));
      

    }


    /* -------------------------------------------------------------------------- */
    /*                              ENVIO REPORTE WS                              */
    /* -------------------------------------------------------------------------- */
    generarReporteVentas(token, idSucursal, nombreSucursal, nombreUsuario, fechaInicial = '', fechaFinal = '') {
      return this.api.postBlob('/transactions/orders/sales-report/pdf', {
        branchId: idSucursal,
        branchName: nombreSucursal,
        endDate: fechaFinal,
        startDate: fechaInicial,
        userName: nombreUsuario
      });
    }

    enviarReporteWS(token, idSucursal, nombreSucursal, nombreUsuario, fechaInicial = '', fechaFinal = ''){
      return this.api.post('/transactions/orders/sales-report/whatsapp/send', {
        branchId: idSucursal,
        branchName: nombreSucursal,
        endDate: fechaFinal,
        startDate: fechaInicial,
        userName: nombreUsuario
      }).pipe(map(() => 'ok'));
      


    }  



     /* -------------------------------------------------------------------------- */
    /*                         OBTENER ORDENES SERVER SIDE                        */
    /* -------------------------------------------------------------------------- */
    dtTomografiaDicom(tipo: 'dicon' | 'normal', page: number, limit: number, search: string) {

      // Paginación server-side. perfil/sucursal los resuelve la sesión (Bearer).
      // `tipo` mapea al searchMode liviano del backend:
      //   'dicon'  -> 'tomografia-dicon' (notificación paciente / DICOM)
      //   'normal' -> 'tomografia'       (notificación doctor / tomografía)
      const searchMode = tipo === 'dicon' ? 'tomografia-dicon' : 'tomografia';
      const limitSeguro = Number(limit) || 10;
      const offset = (Math.max(1, Number(page) || 1) - 1) * limitSeguro;
      const term = (search || '').trim();

      return this.api.post<any>('/transactions/orders/list', {
        limit: limitSeguro,
        offset,
        search: term ? term : 'vacio',
        searchType: 'NOM_CLI',
        status: 'con-imagenes',
        searchMode
      }).pipe(
        map((resp) => ({
          rows: resp?.rows ?? [],
          total: resp?.pagination?.total ?? 0
        }))
      );

    }


    enviarResultadosLaboratorioDoctorNode(orderId) {
      return this.api.post(
        '/transactions/orders/results/laboratory/doctor/email/send',
        { orderId }
      ).pipe(map(() => 'ok'));
    }

    enviarResultadosLaboratorioPacienteNode(orderId) {
      return this.api.post(
        '/transactions/orders/results/laboratory/patient/email/send',
        { orderId }
      ).pipe(map(() => 'ok'));
    }

    enviarResultadosLaboratorioDoctorWhatsappNode(orderId) {
      return this.api.post(
        '/transactions/orders/results/laboratory/doctor/whatsapp/send',
        { orderId }
      ).pipe(map(() => 'ok'));
    }

    enviarResultadosLaboratorioPacienteWhatsappNode(orderId) {
      return this.api.post(
        '/transactions/orders/results/laboratory/patient/whatsapp/send',
        { orderId }
      ).pipe(map(() => 'ok'));
    }

    /* -------------------------------------------------------------------------- */
    /*                      ENVIAR FACTURA POR WHATSAPP                          */
    /* -------------------------------------------------------------------------- */
    enviarFacturaCliente(orderId) {
      return this.api.post(
        '/transactions/orders/invoice/whatsapp/send',
        { orderId }
      ).pipe(map(() => 'ok'));
    }

      /* -------------------------------------------------------------------------- */
      /*                          ENVIAR FACTURA AL CLIENTE                         */
      /* -------------------------------------------------------------------------- */
      enviarFacturaSRI(idOrden) {
        return this.api.post<{
          sri: {
            authorizedAt?: string;
            email?: { ok?: boolean; reason?: string };
            number?: string;
            status: string;
          }
        }>(
          '/transactions/electronic-documents/send-sri-by-order',
          { orderId: Number(idOrden) }
        ).pipe(map(({ sri }) => sri));
      }


       
      /* -------------------------------------------------------------------------- */
      /*                         OBTENER INFORMACION PRUEBAS                        */
      /* -------------------------------------------------------------------------- */
      informacionPruebas(idInformacion){
        return this.api.post('/laboratory/test-info/find', {
          idPrueba: Number(idInformacion)
        }).pipe(catchError(() => of({})));
      }

    /* -------------------------------------------------------------------------- */
    /*                           ENVIAR ACCESOS PACIENTE                          */
    /* -------------------------------------------------------------------------- */
    enviarAccesosPaciente(nombre,
      cedula,
      telefono,
      sucursal
      ){

      return this.api.post<any>('/transactions/orders/patient/access/send', {
        branchId: sucursal,
        identification: cedula,
        name: nombre,
        phone: telefono
      }).pipe(map(() => 'ok'));
      

    }

    /* Subir Resultados — migrados a Node /api/v1 (sucursal se resuelve de la
       sesión Bearer; el 2º parámetro se conserva por compatibilidad de firma).
       Respuesta: { data, recordsTotal, recordsFiltered } (contrato DataTables). */
    private listadoResultados(path: string, estado, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.api.post<Ordenes>(path, {
        status: estado,
        startDate: fechaInicio || '',
        endDate: fechaFin || '',
        offset: Number(pagina) || 0,
        search: busqueda || '',
        limit: Number(limite) || 10
      }).pipe(
        map(resp => resp),
        catchError(err => of(err))
      )
    }

    dtEcografias(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/ecography/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    dtPeriapical(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/periapical/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    dtPanoramica(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/panoramic/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    dtTomografias(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/tomography/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    dtCorporales(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/corporal/reports/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    dtCorporalesDoctores(estado, _sucursal, fechaInicio, fechaFin, pagina, busqueda, limite) {
      return this.listadoResultados('/transactions/orders/results/corporal/images/list', estado, fechaInicio, fechaFin, pagina, busqueda, limite);
    }

    companiasTarjetaDebitoCredito() {
      return this.api.post<any>('/company/card-companies/list', {}).pipe(
        map(resp => resp.rows)
      );
    }

    /* ----------------------- OBTENER ORDENES SERVER SIDE ---------------------- */
    datatableOrdenesFiltro(estado, perfil, sucursal,limite,  pagina, busqueda,anioSeleccionado) {

      return this.api.post<Ordenes>('/transactions/orders/filter/list', {
        status: estado,
        profile: perfil,
        branch: sucursal,
        limit: Number(limite) || 10,
        offset: Number(pagina) || 0,
        search: busqueda || '',
        year: Number(anioSeleccionado)
      }).pipe(
        map(resp => resp),
        catchError(err => of(err))
      )
  
    }

    datatableOrdenesPendienteAnalisis(estado, perfil, sucursal,limite,  pagina, busqueda,anioSeleccionado) {
      return this.api.post<Ordenes>(
        '/transactions/orders/cephalometric/pending/list',
        {
          limit: Number(limite) || 10,
          offset: Number(pagina) || 0,
          search: busqueda || '',
          year: Number(anioSeleccionado)
        }
      ).pipe(
        map(resp => resp),
        catchError(err => of(err))
      );
    }

    datatableOrdenesPendienteCitaPrevia(estado, perfil, sucursal,limite,  pagina, busqueda,anioSeleccionado) {

      return this.api.post<Ordenes>('/transactions/orders/appointments/list', {
        status: estado,
        profile: perfil,
        branch: sucursal,
        limit: Number(limite) || 10,
        offset: Number(pagina) || 0,
        search: busqueda || '',
        year: Number(anioSeleccionado)
      }).pipe(
        map(resp => resp),
        catchError(err => of(err))
      )
  
    }

    validarExistenciaOrdenPendiente(idPaciente,_token){
      return this.api.post('/transactions/orders/patient/pending/validate', {
        patientId: idPaciente
      }).pipe(map(resp => JSON.stringify(resp)));
    }

    anularOrdenesPacienteDoctor(idPaciente,codigoDoctor,token){
      return this.api.post<any>('/transactions/orders/patient/pending/cancel', {
        doctorCode: codigoDoctor,
        patientId: idPaciente
      }).pipe(map(resp => JSON.stringify(resp)));
    }

    getImageServer(idImagen, _token){
      return this.api.post<{ url: string }>(
        '/transactions/orders/results/files/find',
        { fileId: idImagen }
      ).pipe(map(resp => resp.url));
    }

    guardarUrlServidor(key, urlFile: string, usuario, token){
      return this.api.post('/transactions/orders/results/tomography-zip/register', {
        key,
        fileUrl: urlFile,
        userId: usuario,
      }).pipe(
        catchError(this.handleError)
      );
    }


subirArchivoServidorS3(file: File, usuario, token) {

  const formData = new FormData();
  formData.append('file', file);
  formData.append('usuario', usuario);
  formData.append('token', token);

  const url = `${environment.apiBaseUrl}/api/v1/files/upload`;

  return this.http.post<any>(url, formData).pipe(
    map((resp) => resp?.data || resp)
  );
}
        
    private handleError(error: HttpErrorResponse) {
      console.error('Error al subir el archivo', error);
      return throwError('Error al subir el archivo. Por favor intenta nuevamente.');
    }


    cambiarEstadoOrden(idOrden,  token,){

      return this.api.post<any>('/transactions/orders/status/attended', {
        orderId: idOrden
      }).pipe(map(() => 'ok'));
    }


    /* -------------------------------------------------------------------------- */
    /*                         GUARDAR INFORME CORPORAL                           */
    /* -------------------------------------------------------------------------- */
    generarInforme(idOrden, informe, _token){
      return this.api.post('/transactions/rx-reports/body/save', {
        orderId: idOrden,
        report: informe,
        device: this.obtenerDispositivo(),
        platform: (navigator as any).userAgentData?.platform || navigator.platform || '',
        language: navigator.language || '',
        screenResolution: `${window.screen.width}x${window.screen.height}`,
      }).pipe(map(() => 'ok'));
    }

    listarAuditoriaSubidas(limite = 25, offset = 0, busqueda = '', ordenId = '') {
      // El token va en el header Bearer; la respuesta {total, registros} la
      // desenvuelve ApiService (response.data).
      return this.api.post<any>('/transactions/audit-uploads/list', {
        limite,
        offset,
        busqueda,
        ordenId
      });
    }

    private obtenerDispositivo(): string {
      const userAgent = navigator.userAgent || '';
      if (/tablet|ipad/i.test(userAgent)) return 'TABLET';
      if (/mobile|android|iphone/i.test(userAgent)) return 'MOVIL';
      return 'COMPUTADOR';
    }


      /* -------------------------------------------------------------------------- */
    /*                    CARGAR IMAGENES PENDIENTE FACTURADOS                    */
    /* -------------------------------------------------------------------------- */
    subirOrdenFisica(idOrden,ordenFisica,  token){

      return this.api.post<any>('/transactions/orders/physical/upload', {
        orderId: idOrden,
        physicalOrder: ordenFisica
      }).pipe(map(() => 'ok'));
    }
    
    
    uploadFile(file: FormData) {
      return this.http.post<any>(
        `${environment.apiBaseUrl}/api/v1/files/upload`,
        file
      ).pipe(map((resp) => resp?.data || resp));
    }


        /* -------------------------------------------------------------------------- */
    /*                               GUARDAR FACTURA                              */
    /* -------------------------------------------------------------------------- */
    generarDescuento(idOrden, idUsuario, nombreUsuario, metodoPago,subtotalImpuesto,
      subtotalSinImpuesto,
      subtotal,
      iva,
      total, 
      secuencial,
      dineroRecibido,
       dineroEntregado,
       idSucursal,
        nombreSucursal, 
      codigoTarjeta,
      nombreTarjeta,
      jsonPagos,
       token,
      idOferta = "",
      emergency = false,
      observacion
      ){
      const normalizedTotal = this.firstValidMoneyValue(total, subtotal, subtotalSinImpuesto, subtotalImpuesto, '0') || '0';
      const cashReceived = this.firstValidMoneyValue(dineroRecibido, normalizedTotal, '0') || '0';
      const cashReturned = this.firstValidMoneyValue(dineroEntregado, '0') || '0';

      return this.api.post<{
        idOrden: number;
        secuencial: string;
        transaccionId: number;
      }>('/transactions/discounts/create', {
        idOrden,
        nombreUsuario,
        metodoPago,
        subtotalImpuesto,
        subtotalSinImpuesto,
        subtotal,
        iva,
        total,
        secuencial,
        dineroRecibido: cashReceived,
        dineroEntregado: cashReturned,
        idSucursal,
        nombreSucursal,
        codigoTarjeta,
        nombreTarjeta,
        jsonPagos,
        idOferta,
        emergency,
        observacion
      });
    }

    private parseJson(value: any): any {
      if (typeof value !== 'string') return value;
      return JSON.parse(value);
    }

}
